import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input, { Select } from '../../components/ui/Input';
import Table, { THead, TH, TBody, TR, TD } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import { formatDateTime } from '../../utils/formatDate';
import toast from 'react-hot-toast';

const ROLE_COLORS = { admin: 'red', client_manager: 'blue', tech_support: 'cyan', viewer: 'gray' };

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'client_manager', organizationId: '' });
  const [creating, setCreating] = useState(false);

  const loadData = async () => {
    try {
      const [uRes, oRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/organizations'),
      ]);
      setUsers(uRes.data.users);
      setOrgs(oRes.data.organizations);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/users', { ...form, organizationId: parseInt(form.organizationId) });
      toast.success('User created');
      setShowCreate(false);
      setForm({ email: '', password: '', name: '', role: 'client_manager', organizationId: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Users</h1>
          <p className="text-slate-400 mt-1">Manage system users</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ New User</Button>
      </div>

      <Table>
        <THead>
          <tr><TH>Name</TH><TH>Email</TH><TH>Role</TH><TH>Organization</TH><TH>Status</TH><TH>Last Login</TH></tr>
        </THead>
        <TBody>
          {users.map(u => (
            <TR key={u.id}>
              <TD><span className="text-slate-200 font-medium">{u.name}</span></TD>
              <TD><span className="text-sm">{u.email}</span></TD>
              <TD><Badge color={ROLE_COLORS[u.role]}>{u.role.replace('_', ' ')}</Badge></TD>
              <TD>{u.organization?.shortName || u.organization?.name}</TD>
              <TD>
                <span className={`inline-flex items-center gap-1.5 text-xs ${u.isActive ? 'text-emerald-400' : 'text-red-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  {u.isActive ? 'Active' : 'Inactive'}
                </span>
              </TD>
              <TD>{formatDateTime(u.lastLogin)}</TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New User">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <Input label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <Select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="admin">Admin</option>
            <option value="client_manager">Client Manager</option>
            <option value="tech_support">Tech Support</option>
            <option value="viewer">Viewer</option>
          </Select>
          <Select label="Organization" value={form.organizationId} onChange={(e) => setForm({ ...form, organizationId: e.target.value })} required>
            <option value="">Select...</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={creating}>{creating ? 'Creating...' : 'Create User'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
